import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'
import AssetDownload from '@comp/product/asset-download'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    seoTitle: 'BeyondTrust Alternative: Monofor | PAM without per-asset pricing',
    seoDescription:
      'Looking for a BeyondTrust alternative? Monopam delivers vault, session recording, and just-in-time access, with IAM and IGA in the same platform, priced per concurrent session.',
    heroEyebrow: 'BeyondTrust alternative',
    heroTitle: ['Privileged access,', 'without the sticker shock.'],
    heroBody:
      'Monopam covers the PAM core (vault, session recording, rotation, just-in-time approvals) inside a platform that also ships SSO, MFA, and governance. Licensed per concurrent session, deployed in minutes, support included.',
    reasons: [
      {
        title: 'One bill for identity and privilege',
        body: 'BeyondTrust is a PAM product family. Monofor is one platform where privileged access, workforce identity, and governance share a console, a data model, and a renewal conversation.',
      },
      {
        title: 'Pricing that scales with usage, not assets',
        body: 'Per-asset pricing grows with your server count whether anyone touches it or not. Per-concurrent-session licensing tracks real privileged activity: a smaller, more predictable number.',
      },
      {
        title: 'Production in minutes',
        body: 'Browser-based RDP and SSH need nothing installed on endpoints. Discovery imports resources from AD, Azure, and AWS. No professional-services engagement required to get value.',
      },
    ],
    tableTitle: ['Monofor vs BeyondTrust,', 'feature by feature.'],
    tableBody:
      'On day-to-day privileged access the products are at parity. The differences are platform scope, deployment weight, and what the invoice is indexed to.',
    rows: [
      {
        feature: 'Privileged credential vault',
        detail: 'Encrypted storage, credential history, rotation',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Session recording',
        detail: 'Video + keystroke transcript for RDP & SSH',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Browser-based RDP / SSH access',
        detail: 'No client installs or agent fleet',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Just-in-time privileged access',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'IAM + IGA in the same platform',
        detail: 'SSO, MFA, lifecycle, access reviews in one console',
        monofor: true,
        competitor: 'PAM-focused',
      },
      {
        feature: 'Enterprise SSO app catalog',
        detail: '7,000+ pre-built SAML / OIDC / browser apps',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Directory sync engine',
        detail: 'AD, LDAP, HR systems, SQL, custom sources',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Self-hosted deployment',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Per-concurrent-session PAM pricing',
        detail: 'Not per asset, per user, or per module',
        monofor: true,
        competitor: 'Per-asset / quote-based',
      },
      {
        feature: 'Public entry pricing',
        detail: 'IAM from $4 per user / month',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Kubernetes & OpenShift access (native kubectl / oc)',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Database sessions with SQL policy & dynamic masking',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Certificate lifecycle management (CLM) built in',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Customer support included',
        monofor: true,
        competitor: 'Tiered',
      },
    ],
    faqs: [
      {
        q: 'How does Monofor compare to BeyondTrust Password Safe?',
        a: 'The privileged-access fundamentals (vault, rotation, session recording, just-in-time approvals, browser-based access) are covered by both. Monofor adds workforce identity and governance in the same platform, and prices PAM per concurrent session instead of per asset or per user.',
      },
      {
        q: 'What about remote support and endpoint privilege management?',
        a: 'BeyondTrust bundles remote-support tooling and endpoint privilege management as separate products. Monofor focuses on privileged access to infrastructure (vault, sessions, JIT), plus the identity platform around it. If EPM is your primary need, evaluate that requirement separately.',
      },
      {
        q: 'How complex is the migration?',
        a: 'Credentials import into the Monopam vault; resources are discovered from AD, Azure, and AWS; and browser-based sessions mean nothing to install on endpoints. Most teams run their first recorded session within minutes.',
      },
      {
        q: 'Why does the pricing model matter?',
        a: 'Per-asset and per-user PAM pricing punishes growth: every server or admin you add raises the bill. Per-concurrent-session licensing tracks how much privileged access actually happens, which is a much smaller and more predictable number.',
      },
    ],
  },
  tr: {
    seoTitle: 'BeyondTrust Alternatifi: Monofor | Varlık başına fiyatlandırma olmadan PAM',
    seoDescription:
      'BeyondTrust alternatifi mi arıyorsunuz? Monopam; kasa, oturum kaydı ve tam zamanında erişimi (IAM ve IGA aynı platformda) eşzamanlı oturum başına fiyatlandırmayla sunar.',
    heroEyebrow: 'BeyondTrust alternatifi',
    heroTitle: ['Ayrıcalıklı erişim,', 'fatura şoku olmadan.'],
    heroBody:
      'Monopam, PAM çekirdeğini kapsar: kasa, oturum kaydı, rotasyon, tam zamanında onaylar; üstelik SSO, MFA ve yönetişimi de sunan bir platformun içinde. Eşzamanlı oturum başına lisanslanır, dakikalar içinde kurulur, destek dahildir.',
    reasons: [
      {
        title: 'Kimlik ve ayrıcalık için tek fatura',
        body: 'BeyondTrust bir PAM ürün ailesidir. Monofor; ayrıcalıklı erişimin, çalışan kimliğinin ve yönetişimin tek konsolu, tek veri modelini ve tek yenileme görüşmesini paylaştığı tek bir platformdur.',
      },
      {
        title: 'Varlıkla değil, kullanımla ölçeklenen fiyatlandırma',
        body: 'Varlık başına fiyatlandırma, kimse dokunmasa bile sunucu sayınızla birlikte büyür. Eşzamanlı oturum başına lisanslama gerçek ayrıcalıklı etkinliği takip eder: daha küçük, daha öngörülebilir bir sayı.',
      },
      {
        title: 'Günler içinde üretimde',
        body: 'Tarayıcı tabanlı RDP ve SSH, uç noktalara kurulum gerektirmez. Keşif, kaynakları AD, Azure ve AWS’den içe aktarır. Değer elde etmek için profesyonel hizmet projesi gerekmez.',
      },
    ],
    tableTitle: ['Monofor ve BeyondTrust,', 'özellik özellik.'],
    tableBody:
      'Günlük ayrıcalıklı erişimde ürünler başa baş. Farklar; platform kapsamında, kurulum yükünde ve faturanın neye endekslendiğinde.',
    rows: [
      {
        feature: 'Ayrıcalıklı kimlik bilgisi kasası',
        detail: 'Şifreli depolama, kimlik bilgisi geçmişi, rotasyon',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Oturum kaydı',
        detail: 'RDP ve SSH için video + tuş vuruşu dökümü',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Tarayıcı tabanlı RDP / SSH erişimi',
        detail: 'İstemci kurulumu veya ajan filosu yok',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Tam zamanında ayrıcalıklı erişim',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'IAM + IGA aynı platformda',
        detail: 'SSO, MFA, yaşam döngüsü, erişim gözden geçirmeleri tek konsolda',
        monofor: true,
        competitor: 'PAM odaklı',
      },
      {
        feature: 'Kurumsal SSO uygulama kataloğu',
        detail: '7.000+ hazır SAML / OIDC / tarayıcı uygulaması',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Dizin eşitleme motoru',
        detail: 'AD, LDAP, İK sistemleri, SQL, özel kaynaklar',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Kendi altyapınızda kurulum',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Eşzamanlı oturum başına PAM fiyatlandırması',
        detail: 'Varlık, kullanıcı veya modül başına değil',
        monofor: true,
        competitor: 'Varlık başına / teklife dayalı',
      },
      {
        feature: 'Açık başlangıç fiyatı',
        detail: 'IAM kullanıcı başına aylık 4 $’dan başlar',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Kubernetes ve OpenShift erişimi (yerel kubectl / oc)',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'SQL politikası ve dinamik maskelemeli veritabanı oturumları',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Yerleşik sertifika yaşam döngüsü yönetimi (CLM)',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Müşteri desteği dahil',
        monofor: true,
        competitor: 'Kademeli',
      },
    ],
    faqs: [
      {
        q: 'Monofor, BeyondTrust Password Safe ile nasıl karşılaştırılır?',
        a: 'Ayrıcalıklı erişimin temelleri (kasa, rotasyon, oturum kaydı, tam zamanında onaylar, tarayıcı tabanlı erişim) her iki üründe de karşılanıyor. Monofor buna aynı platformda çalışan kimliğini ve yönetişimi ekler ve PAM’i varlık ya da kullanıcı başına değil, eşzamanlı oturum başına fiyatlandırır.',
      },
      {
        q: 'Peki uzaktan destek ve uç nokta ayrıcalık yönetimi?',
        a: 'BeyondTrust, uzaktan destek araçlarını ve uç nokta ayrıcalık yönetimini ayrı ürünler olarak paketler. Monofor, altyapıya ayrıcalıklı erişime odaklanır (kasa, oturumlar, tam zamanında erişim), artı etrafındaki kimlik platformu. EPM birincil ihtiyacınızsa bu gereksinimi ayrıca değerlendirin.',
      },
      {
        q: 'Geçiş ne kadar karmaşık?',
        a: 'Kimlik bilgileri Monopam kasasına aktarılır; kaynaklar AD, Azure ve AWS’den keşfedilir; tarayıcı tabanlı oturumlar sayesinde uç noktalara kurulacak hiçbir şey yoktur. Çoğu ekip ilk kayıtlı oturumunu dakikalar içinde çalıştırır.',
      },
      {
        q: 'Fiyatlandırma modeli neden önemli?',
        a: 'Varlık ve kullanıcı başına PAM fiyatlandırması büyümeyi cezalandırır: eklediğiniz her sunucu veya yönetici faturayı yükseltir. Eşzamanlı oturum başına lisanslama, ayrıcalıklı erişimin gerçekte ne kadar gerçekleştiğini takip eder; bu da çok daha küçük ve daha öngörülebilir bir sayıdır.',
      },
    ],
  },
}

type Props = { locale: string }

export default function BeyondTrustAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      leadMagnet={
        <AssetDownload
          asset="pam-rfp"
          title={{
            en: 'Evaluating PAM vendors? Take the 50 questions.',
            tr: 'PAM tedarikçisi mi değerlendiriyorsunuz? 50 soruyu alın.',
          }}
          copy={{
            en: 'A vendor-neutral RFP template: 50 questions across vault, sessions, JIT, Kubernetes, databases, certificates, EPM, architecture, integrations, and commercials, with a scoring rubric and the red flags to watch for.',
            tr: 'Tedarikçi-bağımsız RFP şablonu: kasa, oturumlar, JIT, Kubernetes, veritabanları, sertifikalar, EPM, mimari, entegrasyonlar ve ticari koşullarda 50 soru; puanlama cetveli ve dikkat edilecek kırmızı bayraklarla.',
          }}
        />
      }
      competitor="BeyondTrust"
      locale={locale}
      bilingual
      path="/compare/beyondtrust-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
